/**
 * API Configuration
 * Base URL and API settings for Drop of Change
 */

// Use your local IP address
export const API_BASE_URL = 'http://localhost:6548/api/v1';
export const API_LEGACY_URL = 'http://localhost:6548';

// API Endpoints
export const BACKEND_HOST = 'https://backend.doc.af'
export const mediaUrl = (path: string = '') => `${BACKEND_HOST}${path}`

// Helper function to resolve image URLs and rewrite domain if needed
export const resolveImageUrl = (url?: string | null): string | null => {
  if (!url) return null;

  const targetHost = new URL(BACKEND_HOST).hostname
  const legacyHosts = new Set([
    'backend.mmo.org.af',
    'mmo.org.af',
    'museum.khwanzay.school',
    'backend.khwanzay.school',
  ])

  // Absolute URL handling
  if (url.startsWith('http')) {
    try {
      const urlObj = new URL(url);
      // If it points to any known legacy host, rewrite host to backend.doc.af
      if (legacyHosts.has(urlObj.hostname)) {
        urlObj.hostname = targetHost;
        urlObj.protocol = 'https:'; // enforce https for backend
        return urlObj.toString();
      }
      // Already a fully-qualified URL on the correct host
      return url;
    } catch {
      // If URL parsing fails, fall through to treat as relative
    }
  }

  // Relative URL handling => prepend backend host
  if (url.startsWith('/')) return `${BACKEND_HOST}${url}`
  return `${BACKEND_HOST}/${url}`
}

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    VERIFY_EMAIL: '/auth/verify-email',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
    REFRESH_TOKEN: '/auth/refresh-token',
    ME: '/auth/me',
  },
  
  // News
  NEWS: {
    BASE: '/news',
    BY_ID: (id: string) => `/news/${id}`,
    BY_SLUG: (slug: string) => `/news/slug/${slug}`,
    FEATURED: '/news/featured',
    LATEST: '/news/latest',
    SEARCH: '/news/search',
  },
  
  // Events
  EVENTS: {
    BASE: '/events',
    BY_ID: (id: string) => `/events/${id}`,
    UPCOMING: '/events/upcoming',
    REGISTER: (id: string) => `/events/${id}/register`,
  },
  
  // Programs
  PROGRAMS: {
    BASE: '/programs',
    BY_ID: (id: string) => `/programs/${id}`,
    ACTIVE: '/programs/active',
    BY_FOCUS_AREA: (focusAreaId: string) => `/programs/focus-area/${focusAreaId}`,
  },
  
  // Donations
  DONATIONS: {
    BASE: '/donations',
    BY_ID: (id: string) => `/donations/${id}`,
    MY_DONATIONS: '/donations/my-donations',
    STATS: '/donations/stats',
  },
  
  // Volunteers
  VOLUNTEERS: {
    BASE: '/volunteers',
    BY_ID: (id: string) => `/volunteers/${id}`,
    STATS: '/volunteers/stats',
    REVIEW: (id: string) => `/volunteers/${id}/review`,
    APPROVE: (id: string) => `/volunteers/${id}/approve`,
    ACTIVATE: (id: string) => `/volunteers/${id}/activate`,
  },
  
  // Team Members
  TEAM_MEMBERS: {
    BASE: '/team-members',
    BY_ID: (id: string) => `/team-members/${id}`,
    BY_DEPARTMENT: (department: string) => `/team-members/department/${department}`,
  },
  
  // Upload
  UPLOAD: {
    IMAGE: '/upload/image',
    IMAGES: '/upload/images',
    DELETE: (filename: string) => `/upload/${filename}`,
    STATS: '/upload/stats',
  },
  
  // Search
  SEARCH: {
    GLOBAL: '/search',
    SUGGESTIONS: '/search/suggestions',
    TRENDING: '/search/trending',
  },
  
  // Experience
  EXPERIENCE: {
    PROJECTS: '/experience/projects',
    PROJECT_BY_ID: (id: string) => `/experience/projects/${id}`,
    FILTERS: '/experience/filters',
    SETTINGS: '/experience/settings',
  },

  // Gallery
  GALLERY: {
    BASE: '/gallery',
    BY_ID: (id: string) => `/gallery/${id}`,
    SEARCH: (query: string) => `/gallery/${query}`,
    FEATURED: '/gallery?featured=true',
    BY_CATEGORY: (category: string) => `/gallery?category=${category}`,
    BY_MEDIA_TYPE: (mediaType: string) => `/gallery?mediaType=${mediaType}`,
    INCREMENT_VIEW: (id: string) => `/gallery/${id}/view`,
    INCREMENT_LIKE: (id: string) => `/gallery/${id}/like`,
    INCREMENT_SHARE: (id: string) => `/gallery/${id}/share`,
  },
} as const;

// Request timeout
export const API_TIMEOUT = 30000; // 30 seconds

// Default language
export const DEFAULT_LANGUAGE = 'en';

// Pagination defaults
export const DEFAULT_PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 100;

export default {
  API_BASE_URL,
  API_LEGACY_URL,
  API_ENDPOINTS,
  API_TIMEOUT,
  DEFAULT_LANGUAGE,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
};
